import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Container, Col, Row } from 'reactstrap';
import moment from 'moment';
import { AgGridReact } from 'ag-grid-react';
import { themeQuartz, colorSchemeDark, colorSchemeLight } from 'ag-grid-community';
import config from '../../../../../config';
import ChartBuilder from '../../../../../shared/components/chart/ChartBuilder';
import CardPanel from '../../../../../shared/components/widgets/CardPanel';
import DateRangePicker from '../../../../../shared/components/widgets/DateRangePicker';
import { getChartTheme } from '../../../../../shared/components/chart/chartThemeHelper';
import { currencyFormatterDirect } from '../../../../../shared/components/table/functions';

export default function CampaignReportLayout() {
  const gridRef = useRef();
  const gridApiRef = useRef(null);
  const sellerSelect = useSelector(store => store.sellerSelect);
  const user = useSelector(store => store.authentication.user);
  const theme = useSelector(store => store.theme);
  const [graphData, setGraphData] = useState([{}]);
  const [apiLoad, setApiLoad] = useState(false);
  const [startDate, setStartDate] = useState(moment().subtract(30, 'days'));
  const [endDate, setEndDate] = useState(moment().subtract(1, 'days'));
  const [tableData, setTableData] = useState([]);

  useEffect(() => {
    if (!sellerSelect.value) return;

    setApiLoad(true);
    setGraphData([{}]);
    setTableData([]);

    const headerWithAuth = { ...config.jsonHeader, authorization: `JWT ${user.jwtToken}` };
    const requestOptions = {
      method: 'GET',
      headers: headerWithAuth,
    };

    const reportUrl = `${config.serverUrl}/target/campaign-report?accountId=${sellerSelect.value}&startDate=${startDate.format('YYYYMMDD')}&endDate=${endDate.format('YYYYMMDD')}`;

    fetch(reportUrl, requestOptions)
      .then((results) => {
        if (!results.ok) {
          throw Error(results.statusText);
        }
        return results.json();
      })
      .then((data) => {
        if (data.status === 'success') {
          // Process graph data
          const formattedGraphData = (data.graphData || []).map(item => {
            // Handle BigQuery date object format or string date
            const dateValue = item.date?.value || item.date;
            return {
              date: dateValue,
              impressions: Number(item.impressions) || 0,
              clicks: Number(item.clicks) || 0,
              spend: Number(item.spend) || 0,
              orders: Number(item.orders) || 0,
              sales: Number(item.sales) || 0,
              reach: Number(item.reach) || 0,
            };
          });
          setGraphData(formattedGraphData);

          // Process table data
          const formattedTableData = (data.tableData || []).map(item => ({
            ...item,
            impressions: Number(item.impressions) || 0,
            clicks: Number(item.clicks) || 0,
            ctr: Number(item.ctr) || 0,
            vendor_cost: Number(item.vendor_cost) || 0,
            actualized_vendor_spend: Number(item.actualized_vendor_spend) || 0,
            total_orders: Number(item.total_orders) || 0,
            total_sales: Number(item.total_sales) || 0,
            total_units: Number(item.total_units) || 0,
            online_orders: Number(item.online_orders) || 0,
            online_sales: Number(item.online_sales) || 0,
            online_units: Number(item.online_units) || 0,
            store_sales: Number(item.store_sales) || 0,
            store_trans: Number(item.store_trans) || 0,
            store_units: Number(item.store_units) || 0,
            roas: Number(item.roas) || 0,
            spm: Number(item.spm) || 0,
            frequency: Number(item.frequency) || 0,
            combined_reach: Number(item.combined_reach) || 0,
            identified_exposures: Number(item.identified_exposures) || 0,
            pacing_percentage: Number(item.pacing_percentage) || 0,
          }));

          // Calculate totals
          const totals = {
            campaign_name: 'TOTAL',
            campaign_id: '',
            account_name: '',
            impressions: formattedTableData.reduce((sum, row) => sum + row.impressions, 0),
            clicks: formattedTableData.reduce((sum, row) => sum + row.clicks, 0),
            reach: formattedTableData.reduce((sum, row) => sum + (row.reach || 0), 0),
            vendor_cost: formattedTableData.reduce((sum, row) => sum + row.vendor_cost, 0),
            actualized_vendor_spend: formattedTableData.reduce((sum, row) => sum + row.actualized_vendor_spend, 0),
            total_orders: formattedTableData.reduce((sum, row) => sum + row.total_orders, 0),
            total_sales: formattedTableData.reduce((sum, row) => sum + row.total_sales, 0),
            total_units: formattedTableData.reduce((sum, row) => sum + row.total_units, 0),
            online_orders: formattedTableData.reduce((sum, row) => sum + row.online_orders, 0),
            online_sales: formattedTableData.reduce((sum, row) => sum + row.online_sales, 0),
            online_units: formattedTableData.reduce((sum, row) => sum + row.online_units, 0),
            store_sales: formattedTableData.reduce((sum, row) => sum + row.store_sales, 0),
            store_trans: formattedTableData.reduce((sum, row) => sum + row.store_trans, 0),
            store_units: formattedTableData.reduce((sum, row) => sum + row.store_units, 0),
            identified_exposures: formattedTableData.reduce((sum, row) => sum + row.identified_exposures, 0),
          };

          // Calculate derived metrics from totals
          totals.ctr = totals.impressions > 0 ? totals.clicks / totals.impressions : 0;
          totals.roas = totals.actualized_vendor_spend > 0 ? totals.total_sales / totals.actualized_vendor_spend : 0;
          totals.spm = totals.impressions > 0 ? (totals.total_sales / totals.impressions) * 1000 : 0;
          totals.frequency = totals.reach > 0 ? totals.identified_exposures / totals.reach : 0;

          // Add totals row to the table
          setTableData([...formattedTableData, totals]);
        }
        setApiLoad(false);
      })
      .catch((error) => {
        console.error('Error fetching Target campaign report:', error);
        setApiLoad(false);
      });
  }, [sellerSelect.value, startDate, endDate]);

  function dateChange(start, end) {
    setStartDate(moment(start));
    setEndDate(moment(end));
  }

  const chartOptions = {
    theme: getChartTheme(theme),
    data: graphData,
    title: {
      text: 'Target Campaign Performance',
    },
    series: [
      {
        type: 'line',
        xKey: 'date',
        yKey: 'impressions',
        yName: 'Impressions',
        tooltip: {
          renderer: (params) => {
            const formatted = params.yValue.toLocaleString();
            return {
              title: params.xValue,
              content: `${params.yName}: ${formatted}`,
            };
          },
        },
      },
      {
        type: 'line',
        xKey: 'date',
        yKey: 'clicks',
        yName: 'Clicks',
        tooltip: {
          renderer: (params) => {
            const formatted = params.yValue.toLocaleString();
            return {
              title: params.xValue,
              content: `${params.yName}: ${formatted}`,
            };
          },
        },
      },
      {
        type: 'line',
        xKey: 'date',
        yKey: 'orders',
        yName: 'Orders',
        tooltip: {
          renderer: (params) => {
            const formatted = params.yValue.toFixed(2);
            return {
              title: params.xValue,
              content: `${params.yName}: ${formatted}`,
            };
          },
        },
      },
      {
        type: 'line',
        xKey: 'date',
        yKey: 'spend',
        yName: 'Spend',
        yKeyAxis: 'ySecondary',
        tooltip: {
          renderer: (params) => {
            const formatted = currencyFormatterDirect(params.yValue);
            return {
              title: params.xValue,
              content: `${params.yName}: ${formatted}`,
            };
          },
        },
      },
      {
        type: 'line',
        xKey: 'date',
        yKey: 'sales',
        yName: 'Sales',
        yKeyAxis: 'ySecondary',
        tooltip: {
          renderer: (params) => {
            const formatted = currencyFormatterDirect(params.yValue);
            return {
              title: params.xValue,
              content: `${params.yName}: ${formatted}`,
            };
          },
        },
      },
    ],
    axes: {
      y: {
        type: 'number',
        position: 'left',
        keys: ['impressions', 'clicks', 'orders'],
        title: {
          text: 'Count',
        },
      },
      ySecondary: {
        type: 'number',
        position: 'right',
        keys: ['spend', 'sales'],
        title: {
          text: 'Amount',
        },
        label: {
          formatter: (params) => currencyFormatterDirect(params.value),
        },
        nice: true,
      },
    },
  };

  // Table column definitions
  const colDef = [
    {
      field: 'campaign_name',
      headerName: 'Campaign Name',
      sortable: true,
      filter: true,
      resizable: true,
      cellStyle: params => {
        // Bold the TOTAL row
        if (params.data && params.data.campaign_name === 'TOTAL') {
          return { fontWeight: 'bold' };
        }
        return null;
      },
    },
    {
      field: 'account_name',
      headerName: 'Account Name',
      sortable: true,
      filter: true,
      resizable: true,
    },
    {
      field: 'campaign_id',
      headerName: 'Campaign ID',
      sortable: true,
      filter: true,
      resizable: true,
    },
    {
      field: 'impressions',
      headerName: 'Impressions',
      sortable: true,
      filter: true,
      resizable: true,
      valueFormatter: (params) => (params.value ? params.value.toLocaleString() : '0'),
    },
    {
      field: 'clicks',
      headerName: 'Clicks',
      sortable: true,
      filter: true,
      resizable: true,
      valueFormatter: (params) => (params.value ? params.value.toLocaleString() : '0'),
    },
    {
      field: 'reach',
      headerName: 'Reach',
      sortable: true,
      filter: true,
      resizable: true,
      valueFormatter: (params) => (params.value ? params.value.toLocaleString() : '0'),
    },
    {
      field: 'ctr',
      headerName: 'CTR',
      sortable: true,
      filter: true,
      resizable: true,
      valueFormatter: (params) => (params.value ? `${(params.value * 100).toFixed(2)}%` : '0.00%'),
    },
    {
      field: 'vendor_cost',
      headerName: 'Vendor Cost',
      sortable: true,
      filter: true,
      resizable: true,
      valueFormatter: (params) => (params.value ? `$${params.value.toFixed(2)}` : '$0.00'),
    },
    {
      field: 'actualized_vendor_spend',
      headerName: 'Actualized Spend',
      sortable: true,
      filter: true,
      resizable: true,
      valueFormatter: (params) => (params.value ? `$${params.value.toFixed(2)}` : '$0.00'),
    },
    {
      field: 'total_orders',
      headerName: 'Total Orders',
      sortable: true,
      filter: true,
      resizable: true,
      valueFormatter: (params) => (params.value ? params.value.toLocaleString() : '0'),
    },
    {
      field: 'total_sales',
      headerName: 'Total Sales',
      sortable: true,
      filter: true,
      resizable: true,
      valueFormatter: (params) => (params.value ? `$${params.value.toFixed(2)}` : '$0.00'),
    },
    {
      field: 'total_units',
      headerName: 'Total Units',
      sortable: true,
      filter: true,
      resizable: true,
      valueFormatter: (params) => (params.value ? params.value.toLocaleString() : '0'),
    },
    {
      field: 'roas',
      headerName: 'ROAS',
      sortable: true,
      filter: true,
      resizable: true,
      valueFormatter: (params) => (params.value ? params.value.toFixed(2) : '0.00'),
    },
    {
      field: 'spm',
      headerName: 'SPM',
      sortable: true,
      filter: true,
      resizable: true,
      valueFormatter: (params) => (params.value ? `$${params.value.toFixed(2)}` : '$0.00'),
    },
    {
      field: 'frequency',
      headerName: 'Frequency',
      sortable: true,
      filter: true,
      resizable: true,
      valueFormatter: (params) => (params.value ? params.value.toFixed(2) : '0.00'),
    },
    {
      field: 'combined_reach',
      headerName: 'Combined Reach',
      sortable: true,
      filter: true,
      resizable: true,
      valueFormatter: (params) => (params.value ? params.value.toLocaleString() : '0'),
    },
  ];

  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
  };

  const onGridReady = useCallback((params) => {
    gridApiRef.current = params.api;
    params.api.sizeColumnsToFit({
      defaultMinWidth: 100,
    });
  }, []);

  const isDarkMode = theme.className && !theme.className.endsWith('light');
  const useTheme = isDarkMode
    ? themeQuartz.withPart(colorSchemeDark)
    : themeQuartz.withPart(colorSchemeLight);

  const onBtnExport = () => {
    if (gridApiRef.current) {
      const fileName = `${sellerSelect.value}-Target-CampaignReport-${moment().format('YYYYMMDD')}.csv`;
      gridApiRef.current.exportDataAsCsv({ fileName });
    }
  };

  return (
    <Container className="dashboard">
      <Col md={12}>
        <CardPanel
          showButtons={false}
          parentRefresh={apiLoad}
        >
          <Row>
            <Col lg={3}>
              <p>Dates:</p>
              <DateRangePicker
                startDate={startDate.toDate()}
                endDate={endDate.toDate()}
                onDateChange={dateChange}
              />
            </Col>
          </Row>
        </CardPanel>
        <Row>
          <CardPanel
            showButtons={false}
            parentRefresh={apiLoad}
          >
            <ChartBuilder chartOptions={chartOptions} />
          </CardPanel>
        </Row>
      </Col>
      <Col md={12}>
        <CardPanel
          title=""
          subhead=""
          showButtons={false}
          parentRefresh={apiLoad}
        >
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
            <button className="btn btn-outline-primary" style={{ width: '200px' }} onClick={onBtnExport} type="button">Export</button>
          </div>
          <div className={useTheme} style={{ height: '50vh', width: '100%' }}>
            <AgGridReact
              theme={useTheme}
              ref={gridRef}
              columnDefs={colDef}
              rowData={tableData}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
            />
          </div>
        </CardPanel>
      </Col>
    </Container>
  );
}
